import type { PdfRendererSource } from "../pdf-renderer/pdf-renderer";
import type { SelectInputOption } from "../select-input/select-input";
import type {
  PdfMergeDocTypeInput,
  PdfMergeDocTypeOption,
  PdfMergeDocument,
  PdfMergePanelTab,
} from "./types";

export const BASE_SOURCE_ID = "base";

export const DEFAULT_TABS: PdfMergePanelTab[] = [
  { id: "merge", label: "Merge and Compress" },
  { id: "compress", label: "Compress" },
];

/** Offered after the types the incoming documents already use. */
export const DEFAULT_DOC_TYPE_OPTIONS: PdfMergeDocTypeOption[] = [
  { value: "radiology_imaging", label: "Radiology Imaging" },
  { value: "laboratory", label: "Laboratory" },
  { value: "clinical_note", label: "Clinical Note" },
  { value: "pathology", label: "Pathology" },
  { value: "operative_report", label: "Operative Report" },
  { value: "discharge_summary", label: "Discharge Summary" },
  { value: "other", label: "Other" },
];

/**
 * A sidebar row.
 *
 * `sourceId` and `pageIndices` always point at the pages inside an original
 * source (the incoming PDF or an uploaded file), never at the rendered output.
 * That is what lets a document sit out of a render and still come back later.
 */
export interface DocumentEntry {
  id: string;
  document: PdfMergeDocument;
  sourceId: string;
  /** Empty until an upload has been opened and its page count is known. */
  pageIndices: number[];
}

export function orderedDocuments(documents: PdfMergeDocument[]): PdfMergeDocument[] {
  return [...documents].sort((a, b) => a.merge_order - b.merge_order);
}

/** 0-based page indices covering an inclusive, 1-based page range. */
export function pageIndices(startPage: number, endPage: number): number[] {
  const indices: number[] = [];
  for (let page = startPage; page <= endPage; page += 1) {
    indices.push(page - 1);
  }
  return indices;
}

export function buildEntries(documents: PdfMergeDocument[]): DocumentEntry[] {
  return orderedDocuments(documents).map((document, index) => ({
    id: `${BASE_SOURCE_ID}-${index}`,
    document,
    sourceId: BASE_SOURCE_ID,
    pageIndices: pageIndices(document.start_page, document.end_page),
  }));
}

/** Every document id in the current sidebar list. */
export function allEntryIds(entries: DocumentEntry[]): Set<string> {
  return new Set(entries.map((entry) => entry.id));
}

/** Initial checkbox state for incoming documents. */
export function initialSelectedIds(
  entries: DocumentEntry[],
  defaultAllSelected: boolean,
): Set<string> {
  return defaultAllSelected ? allEntryIds(entries) : new Set();
}

/**
 * Move the rendered rows to the top, keeping their order so the page numbering
 * assigned afterwards still matches the merged output.
 */
export function renderedFirst(
  entries: DocumentEntry[],
  renderedIds: Set<string>,
): DocumentEntry[] {
  return [
    ...entries.filter((entry) => renderedIds.has(entry.id)),
    ...entries.filter((entry) => !renderedIds.has(entry.id)),
  ];
}

/**
 * Renumber every row against a render containing only `renderedIds`: included
 * documents are laid out back to back from page 1, excluded ones get no pages,
 * and `merge_order` follows the sidebar position either way.
 */
export function renumberEntries(
  entries: DocumentEntry[],
  renderedIds: Set<string>,
): DocumentEntry[] {
  let nextStartPage = 1;

  return entries.map((entry, index) => {
    const pageCount = entry.pageIndices.length;
    const isRendered = renderedIds.has(entry.id);
    const document: PdfMergeDocument = {
      ...entry.document,
      page_count: pageCount,
      start_page: isRendered ? nextStartPage : 0,
      end_page: isRendered ? nextStartPage + pageCount - 1 : 0,
      merge_order: index + 1,
    };
    if (isRendered) nextStartPage += pageCount;
    return { ...entry, document };
  });
}

export function isRenderedDocument(document: PdfMergeDocument): boolean {
  return document.start_page > 0;
}

/** Rows that came from the upload button rather than the incoming PDF. */
export function isUploadEntry(entry: DocumentEntry): boolean {
  return entry.sourceId !== BASE_SOURCE_ID;
}

/**
 * An upload waiting for its type. Such a row cannot be checked, and holds the
 * merge back until the dropdown has been used.
 */
export function needsDocType(entry: DocumentEntry): boolean {
  return isUploadEntry(entry) && entry.document.doc_type === "";
}

/**
 * Checked rows define the merge. Uploads still waiting on a type are pulled in
 * through `previewUploadIds` so they can be read before they are checked.
 */
export function buildRenderedIds(
  resolvedEntries: DocumentEntry[],
  selectedIds: Set<string>,
  previewUploadIds: Set<string>,
): Set<string> {
  const renderedIds = new Set<string>();

  if (selectedIds.size > 0) {
    selectedIds.forEach((id) => renderedIds.add(id));
  } else {
    resolvedEntries
      .filter((entry) => !needsDocType(entry))
      .forEach((entry) => renderedIds.add(entry.id));
  }

  resolvedEntries
    .filter((entry) => previewUploadIds.has(entry.id))
    .forEach((entry) => renderedIds.add(entry.id));

  return renderedIds;
}

/** 1-based first page of `entryId` in the merged PDF's page order. */
export function mergeOrderStartPage(
  mergeOrder: DocumentEntry[],
  entryId: string,
): number | null {
  let nextPage = 1;
  for (const entry of mergeOrder) {
    if (entry.id === entryId) return nextPage;
    nextPage += entry.pageIndices.length;
  }
  return null;
}

/** "radiology_imaging" → "Radiology Imaging", for a type with no label. */
export function docTypeOptionLabel(value: string): string {
  return value
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Options for `SelectInput`, including a value not present in the choice list. */
export function docTypeSelectOptionsFor(
  docType: string,
  choices: PdfMergeDocTypeOption[],
): SelectInputOption[] {
  if (docType && !choices.some((option) => option.value === docType)) {
    return [
      ...choices,
      { value: docType, label: docTypeOptionLabel(docType) },
    ];
  }
  return choices;
}

export function normalizeDocTypeOptions(
  options: PdfMergeDocTypeInput[],
): PdfMergeDocTypeOption[] {
  const normalized = new Map<string, PdfMergeDocTypeOption>();
  options.forEach((option) => {
    const choice =
      typeof option === "string"
        ? { value: option, label: docTypeOptionLabel(option) }
        : option;
    if (choice.value && !normalized.has(choice.value)) {
      normalized.set(choice.value, choice);
    }
  });
  return [...normalized.values()];
}

/**
 * The six public fields of a merge document, plus rotation only when a turn
 * was applied. Download and merge payloads both use this shape.
 */
export function toPublicDocument(document: PdfMergeDocument): PdfMergeDocument {
  const publicDocument: PdfMergeDocument = {
    doc_name: document.doc_name,
    doc_type: document.doc_type,
    start_page: document.start_page,
    end_page: document.end_page,
    page_count: document.page_count,
    merge_order: document.merge_order,
  };
  if (document.rotation) {
    publicDocument.rotation = document.rotation;
  }
  return publicDocument;
}

export function toPublicDocuments(documents: PdfMergeDocument[]): PdfMergeDocument[] {
  return documents.map(toPublicDocument);
}

export async function sourceToArrayBuffer(
  src: PdfRendererSource,
): Promise<ArrayBuffer> {
  if (typeof src === "string") {
    const response = await fetch(src);
    return response.arrayBuffer();
  }
  if (src instanceof Blob) {
    return src.arrayBuffer();
  }
  if (src instanceof ArrayBuffer) {
    return src;
  }
  if (src instanceof Uint8Array) {
    return src.slice().buffer as ArrayBuffer;
  }
  if ("url" in src) {
    const response = await fetch(src.url);
    return response.arrayBuffer();
  }
  return src.buffer;
}
